import { useCallback } from 'react';
import { deleteTaskData, getTaskData, setTaskData } from '../../../libraries/storage/taskStore';
import type { MergedTaskData, TaskData, TaskLevelQuestionnaireData } from '../../../libraries/storage/types';
import type { OutputLabelledDataT } from '../..';
type UseTaskStorage<T, R = MergedTaskData> = {
  updateBackup: (key: string, data: T) => Promise<boolean>;
  getBackup: (key: string) => Promise<TaskData<R> | null>;
  cleanBackup: (key: string) => Promise<boolean>;
};
export const useTaskBackup = (): UseTaskStorage<OutputLabelledDataT, MergedTaskData> => {
  const updateBackup = useCallback(async (key: string, data: OutputLabelledDataT) => {
    const existingData = await getTaskData<MergedTaskData>(key);
    const mergedData: MergedTaskData = {
      ...data,
      taskLevelQuestionnaire: existingData?.data?.taskLevelQuestionnaire
    };
    return setTaskData<MergedTaskData>({
      id: key,
      data: mergedData
    });
  }, []);
  const getBackup = useCallback((key: string) => {
    return getTaskData<MergedTaskData>(key);
  }, []);
  const cleanBackup = useCallback((key: string) => {
    return deleteTaskData(key);
  }, []);
  return {
    updateBackup,
    getBackup,
    cleanBackup
  };
};
export const useTaskLevelQuestionnaireBackup = (): UseTaskStorage<TaskLevelQuestionnaireData, Pick<MergedTaskData, 'taskLevelQuestionnaire'>> => {
  const updateBackup = useCallback(async (key: string, data: TaskLevelQuestionnaireData) => {
    const existingData = await getTaskData<MergedTaskData>(key);
    const mergedData: MergedTaskData = {
      ...(existingData?.data || ({} as OutputLabelledDataT)),
      taskLevelQuestionnaire: data
    };
    return setTaskData<MergedTaskData>({
      id: key,
      data: mergedData
    });
  }, []);
  const getBackup = useCallback(async (key: string) => {
    const taskData = await getTaskData<MergedTaskData>(key);
    if (!taskData?.data?.taskLevelQuestionnaire) {
      return null;
    }
    return {
      ...taskData,
      data: {
        taskLevelQuestionnaire: taskData.data.taskLevelQuestionnaire
      }
    };
  }, []);
  const cleanBackup = useCallback(async (key: string) => {
    const existingData = await getTaskData<MergedTaskData>(key);
    if (!existingData?.data?.taskLevelQuestionnaire) {
      return false;
    }
    const mergedData: MergedTaskData = {
      ...existingData.data,
      taskLevelQuestionnaire: undefined
    };
    return setTaskData<MergedTaskData>({
      id: key,
      data: mergedData
    });
  }, []);
  return {
    updateBackup,
    getBackup,
    cleanBackup
  };
};